import React, { useState, useEffect, useRef } from "react";
import { Card, Col, Row, Typography } from "antd";
import {
  FieldArrayWithId,
  UseFormSetValue,
  Control,
  useWatch,
} from "react-hook-form";
import { DeleteIcon, Edit } from "../../../assets/svg";
import Button from "../../molecules/Button";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { EQARI_GOOGLE_KEY_TRANSLATION } from "../../../configs/constants";
import { useDebounce } from "../../../hooks/useDebounce";

const { Text } = Typography;

const textOnlyEditorConfig = {
  toolbar: {
    items: [
      "bold",
      "italic",
      "underline",
      "|",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "numberedList",
      "bulletedList",
      "|",
      "alignment",
      "|",
      "undo",
      "redo",
    ],
  },
  removePlugins: [
    "Image",
    "ImageToolbar",
    "ImageUpload",
    "ImageInsert",
    "MediaEmbed",
    "Table",
    "EasyImage",
    "CKFinder",
  ],
};

type AdditionalTerm = {
  english: string;
  arabic: string;
};

interface AdditionalTermsFieldsProps {
  fields: FieldArrayWithId<
    { additionalTerms: AdditionalTerm[] },
    "additionalTerms",
    "id"
  >[];
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  remove: (index: number) => void;
  isReadOnly: boolean;
}

const translate = async (
  text: string,
  fieldPath: string,
  setValue: UseFormSetValue<any>
) => {
  if (!text?.trim()) return;

  const params = new URLSearchParams({
    q: text,
    source: "en",
    target: "ar",
    key: EQARI_GOOGLE_KEY_TRANSLATION,
  });

  const res = await fetch(
    `https://translation.googleapis.com/language/translate/v2?${params.toString()}`
  );

  const data = await res.json();
  const translatedText =
    data?.data?.translations?.[0]?.translatedText || "";

  setValue(fieldPath, translatedText, { shouldDirty: true });
};

export const AdditionalTermsFields: React.FC<AdditionalTermsFieldsProps> = ({
  fields,
  control,
  setValue,
  remove,
  isReadOnly,
}) => {
  const watchedTerms = useWatch({
    control,
    name: "additionalTerms",
  });
  const lastTranslatedEnglishRef = useRef<Record<number, string>>({});
  const debouncedTerms = useDebounce(watchedTerms, 700);

  const [prefilledIds, setPrefilledIds] = useState<Set<string>>(new Set());
  const [editableIndex, setEditableIndex] = useState<number | null>(null);
  
  const [translationEnabledForIndex, setTranslationEnabledForIndex] = useState<Set<number>>(new Set());

  const hasInitialized = useRef(false);
  const previousFieldCount = useRef(0);

  useEffect(() => {
    if (hasInitialized.current) return;
    if (!watchedTerms || watchedTerms.length === 0) return;

    const initialPrefilled = new Set<string>();

    fields.forEach((field, index) => {
      const english = watchedTerms[index]?.english;
      const arabic = watchedTerms[index]?.arabic;

      const hasBackendData =
        (english && english !== "<p></p>") ||
        (arabic && arabic !== "<p></p>");

      if (hasBackendData) {
        initialPrefilled.add(field.id);
      }
    });

    setPrefilledIds(initialPrefilled);
    previousFieldCount.current = fields.length;
    hasInitialized.current = true;
  }, [fields, watchedTerms]);

  useEffect(() => {
    if (fields.length > previousFieldCount.current) {
      const newFieldIndex = fields.length - 1;
      
      setTranslationEnabledForIndex(prev => new Set(prev).add(newFieldIndex));
      setTimeout(() => {
        const lastCard = document.querySelector(
          `[data-field-id="${fields[newFieldIndex]?.id}"]`
        );
        if (lastCard) {
          lastCard.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }

    previousFieldCount.current = fields.length;
  }, [fields.length]);

  useEffect(() => {
    if (!debouncedTerms) return;

    debouncedTerms.forEach((term, index) => {
      if (!term?.english?.trim()) return;

      // Only translate if translation is enabled for this index
      if (!translationEnabledForIndex.has(index)) return;

      const fieldId = fields[index]?.id;
      const isPrefilled = prefilledIds.has(fieldId);
      const canTranslate =
        !isPrefilled || editableIndex === index;

      if (!canTranslate) return;

      const lastEnglish = lastTranslatedEnglishRef.current[index];

      if (lastEnglish === term.english) return;

      lastTranslatedEnglishRef.current[index] = term.english;

      translate(
        term.english,
        `additionalTerms.${index}.arabic`,
        setValue
      );
    });
  }, [
    debouncedTerms,
    editableIndex,
    prefilledIds,
    fields,
    setValue,
    translationEnabledForIndex,
  ]);

  const handleEditClick = (index: number) => {
    const newEditableIndex = editableIndex === index ? null : index;
    setEditableIndex(newEditableIndex);
    
    if (newEditableIndex === index) {
      setTranslationEnabledForIndex(prev => new Set(prev).add(index));
    }
  };

  const handleEnglishChange = (index: number, data: string) => {
    setTranslationEnabledForIndex(prev => new Set(prev).add(index));
    
    setValue(
      `additionalTerms.${index}.english`,
      data,
      { shouldDirty: true }
    );
  };

  return (
    <Row gutter={[0, 16]}>
      {fields.map((field, index) => {
        const isPrefilled = prefilledIds.has(field.id);

        const isEditable =
          !isReadOnly && (!isPrefilled || editableIndex === index);

        return (
          <Col span={24} key={field.id}>
            <Card bodyStyle={{ padding: 16 }} data-field-id={field.id}>
              <Row justify="space-between" align="middle">
                <Text strong>Term {index + 1}</Text>

                <div style={{ display: "flex", gap: 8 }}>
                  {isPrefilled && (
                    <Button
                      label={editableIndex === index ? "Done Editing" : "Edit"}
                      icon={<Edit width="18px" height="18px" style={{ marginRight: "8px" }} />}
                      disabled={isReadOnly}
                      onClick={() => handleEditClick(index)}
                      className="change-button"
                    />
                  )}

                  <Button
                    label="Delete"
                    icon={
                      <DeleteIcon
                        width="18px"
                        height="18px"
                        style={{ marginRight: "8px" }}
                      />
                    }
                    disabled={isReadOnly}
                    onClick={() => {
                      remove(index);
                      if (editableIndex === index) {
                        setEditableIndex(null);
                      }
                      setTranslationEnabledForIndex(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(index);
                        return newSet;
                      });
                    }}
                    className="change-button"
                  />
                </div>
              </Row>

              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Text>English</Text>
                  <CKEditor
                    editor={ClassicEditor}
                    data={watchedTerms?.[index]?.english || ""}
                    disabled={!isEditable}
                    config={{ ...textOnlyEditorConfig, language: "en" }}
                    onChange={(_, editor) => {
                      if (!isEditable) return;
                      handleEnglishChange(index, editor.getData());
                    }}
                  />
                </Col>

                <Col span={12}>
                  <Text>Arabic</Text>
                  <CKEditor
                    editor={ClassicEditor}
                    data={watchedTerms?.[index]?.arabic || "<p></p>"}
                    disabled={!isEditable}
                    config={{ ...textOnlyEditorConfig, language: "ar" }}
                    onReady={(editor) => {
                      editor.editing.view.change((writer) => {
                        const root =
                          editor.editing.view.document.getRoot();
                        writer.setStyle("direction", "rtl", root);
                        writer.setStyle("text-align", "right", root);
                      });
                    }}
                    onChange={(_, editor) => {
                      if (!isEditable) return;

                      setValue(
                        `additionalTerms.${index}.arabic`,
                        editor.getData(),
                        { shouldDirty: true }
                      );
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};
